import { z } from 'zod';

import { CancellationPolicyCutoffUnit, GuestCancellationBookingStatus } from './types';
import { LocalDateTimeStringSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint } from '../common';
import { MediaSchema } from '../resource/common';

export const CancellationPolicySchema = z.object({
  active: z.boolean(),
  cutoffValue: z.number().min(0),
  cutoffUnit: z.nativeEnum(CancellationPolicyCutoffUnit),
  refundPercentage: z.number().min(0).max(100),
  notes: z.string().nonempty(),
});
const PostCancellationPolicyRequestSchema = CancellationPolicySchema;
const PostCancellationPolicyResponseSchema = z.object({
  id: z.string().describe('The id of the created cancellation policy'),
});
export const PostCancellationPolicyEndpoint = makeEndpoint({
  title: 'New cancellation policy',
  description: 'Create a new cancellation policy for a brand.',
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/cancellation-policies`,
  requestSchema: PostCancellationPolicyRequestSchema,
  responseSchema: PostCancellationPolicyResponseSchema,
});
const GetCancellationPolicyResponseSchema = z
  .object({
    id: z.string().uuid(),
    active: z.boolean(),
    cutoffValue: z.number().min(0),
    cutoffUnit: z.nativeEnum(CancellationPolicyCutoffUnit),
    refundPercentage: z.number().min(0).max(100),
    notes: z.string(),
  })
  .describe('Get all the cancellation policies.');
export const GetCancellationPolicyEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, cancellationPolicyId }: { brandId: string; cancellationPolicyId: string }) =>
    `v1/brands/${brandId}/cancellation-policies/${cancellationPolicyId}`,
  requestSchema: z.void(),
  responseSchema: GetCancellationPolicyResponseSchema,
});
export const GetCancellationPolicyPublicEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, cancellationPolicyId }: { brandId: string; cancellationPolicyId: string }) =>
    `v1/brands/${brandId}/cancellation-policies/${cancellationPolicyId}`,
  requestSchema: z.void(),
  responseSchema: GetCancellationPolicyResponseSchema,
});
export const GetCancellationPoliciesEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/cancellation-policies`,
  requestSchema: z.void(),
  responseSchema: z.array(GetCancellationPolicyResponseSchema),
});

const GetBookingsForGuestCancellationResponseSchema = z
  .array(
    z.object({
      bookingId: z.string().uuid(),
      title: z.string(),
      coverMedia: MediaSchema.omit({ order: true }).required({ kind: true }),
      refundPercentage: z.number().min(0).max(100).nullable(),
      notes: z.string().nullable(),
      sessionDateTime: LocalDateTimeStringSchema,
      sessionDurationInMinutes: z.number(),
      status: z.nativeEnum(GuestCancellationBookingStatus),
      totalAmount: z.number(),
      refundAmount: z.number(),
      previouslyRefundedAmount: z.number(),
      remainingBalance: z.number(),
    }),
  )
  .describe('List the bookings associated to a cart id for guest cancellation.');

const GetBookingsForGuestCancellationQuerySchema = z.object({ token: z.string().nonempty() });

export const GetBookingsForGuestCancellationEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    cartId,
    query,
  }: {
    brandId: string;
    cartId: string;
    query: z.input<typeof GetBookingsForGuestCancellationQuerySchema>;
  }) => `v1/brands/${brandId}/carts/${cartId}/guest-cancellation?${buildQueryString(query)}`,
  querySchema: GetBookingsForGuestCancellationQuerySchema,
  requestSchema: z.void(),
  responseSchema: GetBookingsForGuestCancellationResponseSchema,
});

export const PostBookingCancellationEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ bookingId }: { bookingId: string }) => `v1/bookings/${bookingId}/guest-cancellation`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

const PostGuestCancellationRequestSchema = z.object({
  bookingId: z.string().uuid(),
  token: z.string().nonempty(),
});
export const PostGuestCancellationEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, cartId }: { brandId: string; cartId: string }) =>
    `v1/brands/${brandId}/carts/${cartId}/guest-cancellation`,
  requestSchema: PostGuestCancellationRequestSchema,
  responseSchema: z.void(),
});

const CheckBookingCancellableResponseSchema = z.object({
  isCancellable: z.boolean(),
  status: z.nativeEnum(GuestCancellationBookingStatus),
});

const CheckBookingCancellableQuerySchema = z.object({
  providerBrandId: z.string().uuid().optional(),
});

export const CheckBookingCancellableEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ bookingId, query }: { bookingId: string; query: z.input<typeof CheckBookingCancellableQuerySchema> }) =>
    `v1/bookings/${bookingId}/cancellable?${buildQueryString(query)}`,
  querySchema: CheckBookingCancellableQuerySchema,
  requestSchema: z.void(),
  responseSchema: CheckBookingCancellableResponseSchema,
});
